import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { useAllSearchParams, useSearch } from '~libs/searchParams';

import { ClickableIcon, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { Row, TableView } from '~components/table-view';
import CutoverMandateBanner from '~support-at-home/components/config/service-cutover/CutoverMandateBanner';
import ServiceCutover, { CutoverPrefill } from '~support-at-home/components/config/service-cutover/ServiceCutover';

import columnsFactory from '~support-at-home/forms/sah-associations/list';
import { createSearchFields } from '~support-at-home/forms/sah-associations/search';

import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getContributionCategories } from '~lookups/actions/sahCategories';
import { useGetCutoverMandateQuery } from '~support-at-home/actions/sahServiceCutover';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { setTitle } from '~main/actions/login';
import {
  useGetAssignableServiceTypesQuery,
  useGetAssocsQuery,
  useUpdateAssocMutation,
} from '~support-at-home/actions/sahServiceTypeAssociations';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectUserTags } from '~main/selectors/login';

const SahServiceAssociations: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const userTags = useAppSelector(selectUserTags);

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  // Set by the banner's action and cleared by the assistant once it has been taken, so the toolbar
  // icon still opens an empty assistant afterwards.
  const [cutoverPrefill, setCutoverPrefill] = useState<CutoverPrefill | null>(null);

  const searchParamValues = useAllSearchParams();
  const { sah_service_id, service_type_id } = searchParamValues;
  const initialSearchState: any = {
    sah_service_id: sah_service_id ?? undefined,
    service_type_id: service_type_id ?? undefined,
  };

  const [searchParams, setSearchParams] = useSearch('search', initialSearchState);
  const [params, setParams] = useState({ limit: 30 });

  // RTK Queries
  const { data: sahServicesData = [] } = useGetSahServicesQuery({}, { skip: !isActive });
  const { data: cutoverMandate, refetch: refetchMandate } = useGetCutoverMandateQuery(undefined, {
    skip: !isActive,
  });
  const { data: assignableServiceTypes = [] } = useGetAssignableServiceTypesQuery({}, { skip: !isActive });
  const {
    data: assocsData,
    isLoading,
    refetch,
  } = useGetAssocsQuery({ ...params, ...searchParams }, { skip: !isActive, refetchOnMountOrArgChange: true });
  const [updateAssoc] = useUpdateAssocMutation();

  const searchFields = createSearchFields(sahServicesData);
  const [data, setData] = useState<Row[]>(assocsData?.rows ?? []);

  useEffect(() => {
    if (isActive) {
      setTitle('SaH Service Associations');
      dispatch(getContributionCategories({ sort: ['sequence'] }));
      dispatch(getServiceTypes());
    }
  }, [isActive, dispatch]);

  useEffect(() => {
    setData(assocsData?.rows ?? []);
  }, [assocsData]);

  const load = (loadParams: { [key: string]: any } = {}) => {
    setParams((prev) => ({ ...prev, ...loadParams }));
  };

  // Returns the promise so TableView can hold the row in edit mode, spinner showing, until the
  // save actually lands. `.unwrap()` is what makes a rejected save reject here rather than
  // resolving with an `error` property - without it every save "succeeds" and the row leaves edit
  // mode even when the server refused it (an overlapping-period association, for instance).
  const handleUpdateRow = async (row: Row) => {
    const { id, service_type_ids } = row;
    await updateAssoc({ sah_service_id: id, service_type_ids }).unwrap();
    // The mandate is resolved from these associations, so editing one changes both how many services
    // it reports and which pairs it would offer. It lives in its own API slice, so the association
    // save cannot invalidate it by tag - it has to be re-read here.
    refetchMandate();
  };

  const handleChange = (rows: Row[]) => {
    setData(rows);
  };

  const columns = columnsFactory(serviceTypes, new Set(assignableServiceTypes.map((type) => type.id)));

  const canUpdateBudgets = userTags.includes(AuthTags.CLIENTS.BUDGETS.UPDATE);

  // Read again on the way in rather than prefilling from whatever was fetched when the screen
  // loaded. The pairs are resolved from the associations on this very screen, and a provider who
  // adds the missing one and then starts the assistant would otherwise be handed the run as it
  // stood before their edit - short exactly the service they had just added.
  const startMandatedCutover = async () => {
    const { data: fresh } = await refetchMandate();
    const mandate = fresh ?? cutoverMandate;

    setCutoverPrefill({
      cutover_date: mandate?.cutover_date ?? '',
      pairs: mandate?.pairs ?? [],
    });
  };

  const search = (values: { [key: string]: any }) => setSearchParams(values);

  return (
    <div className="p-2">
      <div>
        {/* Between the tab and the toolbar: this states what the assistant in the toolbar is for
            before a provider reaches the control that starts it. Renders nothing where there is no
            mandated change to offer. */}
        <CutoverMandateBanner
          mandate={cutoverMandate}
          disabled={!canUpdateBudgets}
          onStart={startMandatedCutover}
        />
        <Toolbar
          id="service-associations"
          rightCluster={
            <>
              <ClickableIcon
                id="sah-client-contributions-search-button"
                icon={faMagnifyingGlass}
                onClick={() => setSearchBarOpen(!searchBarOpen)}
                toolTip="Toggle Search Bar"
                // An icon-only `role="button"` with no label reaches assistive technology unnamed -
                // axe flags it as aria-command-name. The tooltip does not supply the name: it is a
                // separate portaled element, not an accessible label. Same fix as EditAction's icons.
                ariaLabel="Toggle Search Bar"
              />
              {/* The cutover assistant starts here because this screen is where a provider does the
                  first part of the same work by hand. It only accelerates what these screens already
                  allow, so it is gated on the same permission the rows on this screen are. */}
              <ServiceCutover
                canUpdate={canUpdateBudgets}
                onApplied={() => {
                  refetch();
                  refetchMandate();
                }}
                prefill={cutoverPrefill}
                onPrefillConsumed={() => setCutoverPrefill(null)}
              />
            </>
          }
        />
        <SearchBar isOpen={searchBarOpen} fields={searchFields} onSubmit={search} />
        <TableView
          id={'sah_service_assocs_table'}
          data={data}
          columns={columns}
          emptyMessage={'No SAH Service Types found'}
          allowPagination={true}
          totalCount={assocsData?.total ?? 0}
          onLoadMore={() => load({ limit: params.limit + 30 })}
          canDeleteRow={false}
          onSaveRow={handleUpdateRow}
          onChange={handleChange}
          isLoading={isLoading}
          showAddRow={false}
        />
      </div>
    </div>
  );
};

export default SahServiceAssociations;
